import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { CloudUpload, Phone, Trash2, UserRound } from "lucide-react";
import { getDatabase, push, ref, update } from "firebase/database";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
  getStorage,
} from "firebase/storage";

import { useNotification } from "../../contexts/NotificationContext";
import { Button } from "../../components/ui/button";

interface Props {
  id?: string;
  driver?: any;
}

export default function DriverForm({ id, driver }: Props) {
  const [data, setData] = useState({ name: "", phone: "", photo: "" });
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [key, setKey] = useState<string | null>(null);

  const { showNotification } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    if (id && driver) {
      setKey(id);
      setData({
        name: driver.name || "",
        phone: driver.phone || "",
        photo: driver.photo || "",
      });
    }
  }, [id, driver]);

  const handleChange = (field: string, value: string) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleFileRemove = () => {
    setFile(null);
    setData((prev) => ({ ...prev, photo: "" }));
  };

  const uploadPhoto = (path: string) => {
    return new Promise<string>((resolve, reject) => {
      if (!file) {
        reject("missing file");
        return;
      }
      const storage = getStorage();
      const fileRef = storageRef(storage, path);
      const uploadTask = uploadBytesResumable(fileRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const value = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(value);
        },
        reject,
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  const handleSubmit = async () => {
    if (!data.name || !data.phone || (!file && !data.photo)) {
      return showNotification(
        "warning",
        "Por favor, preencha todos os campos obrigatórios."
      );
    }

    try {
      const db = getDatabase();
      const driversRef = ref(db, "drivers");
      const newDriverRef = key
        ? ref(db, `drivers/${key}`)
        : await push(driversRef, data);
      let photoURL = data.photo;

      if (file) {
        setProgress(1);
        photoURL = await uploadPhoto(`drivers/${key || newDriverRef.key}`);
      }

      await update(newDriverRef, { ...data, photo: photoURL });
      showNotification(
        "success",
        key ? "Motorista atualizado." : "Motorista cadastrado com sucesso."
      );
      navigate("/admin-management");
    } catch (err) {
      setProgress(0);
      showNotification("error", "Ocorreu um erro ao salvar o motorista.");
    }
  };

  return (
    <form
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <div className="space-y-2">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-white/60">
          Foto *
        </p>
        <div className="flex items-center gap-3">
          <div className="h-16 w-16 overflow-hidden rounded-full border border-white/20 bg-white/5">
            {(file || data.photo) && (
              <img
                src={(file && URL.createObjectURL(file)) || data.photo}
                alt={data.name}
                className="h-full w-full object-cover"
              />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="bg-white text-slate-900 hover:bg-white/80"
            >
              <label className="flex cursor-pointer items-center gap-2">
                <CloudUpload className="h-4 w-4" /> Inserir foto
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={!file && !data.photo}
              onClick={handleFileRemove}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Remover
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-white">Nome *</label>
        <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3">
          <UserRound className="h-4 w-4 text-white/60" />
          <input
            value={data.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Nome do motorista"
            className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-white/40"
          />
        </div>
        {!data.name && (
          <p className="text-xs text-red-400">Este campo é obrigatório.</p>
        )}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-white">Telefone *</label>
        <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3">
          <Phone className="h-4 w-4 text-white/60" />
          <input
            value={data.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            placeholder="Telefone"
            className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-white/40"
          />
        </div>
        {!data.phone && (
          <p className="text-xs text-red-400">Este campo é obrigatório.</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={progress > 0 && progress < 100}
        className="w-full"
      >
        {key ? "Atualizar" : "Adicionar"}
      </Button>
    </form>
  );
}
